<script setup>
// Onboarding WiFi step (MDL-324). Wraps the existing WifiForm so the wizard
// reuses one WiFi editor across both editors. WifiForm mutates `settings` in
// place (flat wifi_* keys), and the wizard persists the whole config on exit —
// so nothing is applied live; the device picks up WiFi changes on its next
// restart. See spec 144080898 §2.1.

import { computed, watchEffect } from 'vue';
import WifiForm from '../WifiForm.vue';
import {
  apSecurityState,
  DEFAULT_AP_PASSWORD,
  WIFI_AP_PASSWORD_MIN_LENGTH,
  WIFI_AP_PASSWORD_MAX_LENGTH,
} from '../../js/wifiValidation.js';

const props = defineProps({
  // The device config's `settings` object; WifiForm writes wifi_* keys onto it.
  settings: {
    type: Object,
    required: true,
  },
});

// update:invalid lets the wizard block Next/Skip while the AP password can't
// actually be saved — see the 'invalid' state below.
const emit = defineEmits(['update:invalid']);

// 'invalid' | 'open' | 'default' | 'secure', from the shared helper both this
// step and OpenApWarning now use. Previously each derived its own answer from
// a comment promising they were "kept in sync manually"; they drifted, and
// this step went on calling the shipped passphrase secure long after the
// warning stopped (MDL-387).
const state = computed(() => apSecurityState(props.settings));

// An out-of-range password isn't just "insecure", it's un-saveable: the
// sanitiser rejects it and the device's access point never starts (see
// wifi_ap_password validation in config_validator.py), so it must block
// leaving the step, not just warn.
watchEffect(() => emit('update:invalid', state.value === 'invalid'));

// 'default' is alert-info to match OpenApWarning, which uses the same blue for
// the same state. The two banners say the same thing about the same condition,
// so they should not look like different severities depending on which screen
// the owner happens to be on.
const alertClass = computed(() => ({
  invalid: 'alert-error',
  open: 'alert-error',
  default: 'alert-info',
  secure: 'alert-success',
}[state.value]));

const defaultApPassword = DEFAULT_AP_PASSWORD;
const minLength = WIFI_AP_PASSWORD_MIN_LENGTH;
const maxLength = WIFI_AP_PASSWORD_MAX_LENGTH;
</script>

<template>
  <div class="space-y-4">
    <p class="text-xl text-center p-2">
      Connecting over Wifi can make it easier to configure Midulate without taking the device off your pedalboard.
    </p>

    <p class="text-base-content/80 text-center p-2">
      Midulate has 2 Wifi configuration options. It can connect to your Wifi network or it can broadcast its own network.
      Any changes you make below will take effect next time your device restarts.
    </p>

    <div
      role="alert"
      :class="['alert', alertClass]"
      data-testid="wifi-security-alert"
      :data-state="state"
    >
      <svg v-if="state === 'secure'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>

      <!-- Says what is actually wrong. The old wording claimed no password was
           set even when one was simply too short, contradicting the field
           error right beside it. -->
      <span v-if="state === 'invalid'">
        <strong>Midulate's own network won't start.</strong>
        The broadcast mode password must be between {{ minLength }} and {{ maxLength }} characters.
        Correct it below to continue.
      </span>

      <span v-else-if="state === 'open'">
        <strong>Broadcast mode has no password.</strong>
        Midulate's own network would be open to anyone nearby.
        Set a password below, or turn off broadcast mode.
      </span>

      <!-- Raised here rather than after onboarding: the owner is already
           editing WiFi, so this is the moment they can act on it. -->
      <span v-else-if="state === 'default'">
        <strong>Midulate is still using the password it shipped with</strong> (<code>{{ defaultApPassword }}</code>).
        It's printed in the manual, so anyone can look it up.
        Set your own below to make this network private.
      </span>

      <span v-else>Your device is secure.</span>
    </div>

    <WifiForm :settings="settings" />
  </div>
</template>
